import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo, ReactNode } from 'react';
import type { FeedbackConfig, FeedbackContext as FeedbackContextType, SubmissionState } from './types';
import { collectBrowserContext } from './browserContext';
import { ErrorCollector, redactSensitiveData, sanitizeUrl } from './errorCollector';
import { captureScreenshot } from './screenshotCapture';

interface FeedbackContextValue {
  config: Required<Pick<FeedbackConfig, 'apiEndpoint' | 'projectKey' | 'issueType' | 'zIndex'>> & FeedbackConfig;
  isOpen: boolean;
  openForm: () => Promise<void>;
  closeForm: () => void;
  submissionState: SubmissionState;
  setSubmissionState: (state: SubmissionState) => void;
  errorMessage: string | null;
  setErrorMessage: (message: string | null) => void;
  successMessage: string | null;
  setSuccessMessage: (message: string | null) => void;
  getContext: () => Promise<FeedbackContextType>;
  /** Current screenshot (base64), null if not captured */
  screenshot: string | null;
  /** Whether screenshot is being captured */
  isCapturingScreenshot: boolean;
}

const FeedbackCtx = createContext<FeedbackContextValue | null>(null);

interface FeedbackProviderProps {
  config: FeedbackConfig;
  children: ReactNode;
}

const DEFAULT_CONFIG = {
  apiEndpoint: '/api/feedback',
  issueType: 'Bug',
  zIndex: 9999,
  position: { bottom: 24, right: 24 },
  enableScreenshot: true,
  enableErrorTracking: true,
  maxRecentErrors: 5,
};

export function FeedbackProvider({ config, children }: FeedbackProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);

  // Error collector ref
  const errorCollectorRef = useRef<ErrorCollector | null>(null);

  const mergedConfig = useMemo(() => ({
    ...DEFAULT_CONFIG,
    ...config,
    position: { ...DEFAULT_CONFIG.position, ...config.position },
  }), [config]);

  // Initialize error collector
  useEffect(() => {
    if (mergedConfig.enableErrorTracking !== false) {
      errorCollectorRef.current = new ErrorCollector(mergedConfig.maxRecentErrors);
      errorCollectorRef.current.initialize();

      return () => {
        errorCollectorRef.current?.cleanup();
      };
    }
  }, [mergedConfig.enableErrorTracking, mergedConfig.maxRecentErrors]);

  const openForm = useCallback(async () => {
    // Capture screenshot BEFORE opening dialog
    if (mergedConfig.enableScreenshot !== false) {
      setIsCapturingScreenshot(true);
      try {
        const captured = await captureScreenshot();
        setScreenshot(captured);
      } catch (error) {
        console.error('[FeedbackWidget] Screenshot capture failed:', error);
        setScreenshot(null);
      }
      setIsCapturingScreenshot(false);
    }

    setIsOpen(true);
    setSubmissionState('idle');
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [mergedConfig.enableScreenshot]);

  // Listen for shell's report bug event (cross Module Federation boundary)
  useEffect(() => {
    const handleOpenFeedbackForm = () => {
      openForm();
    };

    window.addEventListener('open-feedback-form', handleOpenFeedbackForm);
    return () => {
      window.removeEventListener('open-feedback-form', handleOpenFeedbackForm);
    };
  }, [openForm]);

  const closeForm = useCallback(() => {
    setIsOpen(false);
    setSubmissionState('idle');
    setErrorMessage(null);
    setSuccessMessage(null);
    setScreenshot(null);
  }, []);

  const getContext = useCallback(async (): Promise<FeedbackContextType> => {
    const browserContext = collectBrowserContext();

    let customContext: Partial<FeedbackContextType> = {};
    if (config.contextCollector) {
      try {
        customContext = await config.contextCollector();
      } catch (error) {
        console.error('Error collecting custom context:', error);
      }
    }

    // Get errors from collector
    const recentErrors = errorCollectorRef.current?.getErrors().map((err) => ({
      ...err,
      message: redactSensitiveData(err.message),
      stack: err.stack ? redactSensitiveData(err.stack) : undefined,
    })) ?? [];

    const consoleErrors = errorCollectorRef.current?.getConsoleErrors().map(redactSensitiveData) ?? [];

    // Sanitize the route/URL
    const route = customContext.route ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
    const sanitizedUrl = typeof window !== 'undefined' ? sanitizeUrl(window.location.href) : route;

    return {
      user: customContext.user ?? { email: 'unknown', displayName: 'Unknown User' },
      route: sanitizedUrl,
      timestamp: new Date().toISOString(),
      userAgent: browserContext.userAgent,
      browserInfo: browserContext.browserInfo,
      osInfo: browserContext.osInfo,
      viewport: typeof window !== 'undefined' ? {
        width: window.innerWidth,
        height: window.innerHeight,
      } : undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: typeof navigator !== 'undefined' ? navigator.language : undefined,
      recentErrors: recentErrors.length > 0 ? recentErrors : undefined,
      consoleErrors: consoleErrors.length > 0 ? consoleErrors : undefined,
      ...customContext,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.contextCollector]);

  const value = useMemo<FeedbackContextValue>(() => ({
    config: mergedConfig,
    isOpen,
    openForm,
    closeForm,
    submissionState,
    setSubmissionState,
    errorMessage,
    setErrorMessage,
    successMessage,
    setSuccessMessage,
    getContext,
    screenshot,
    isCapturingScreenshot,
  }), [
    mergedConfig, isOpen, openForm, closeForm, submissionState, setSubmissionState,
    errorMessage, setErrorMessage, successMessage, setSuccessMessage,
    getContext, screenshot, isCapturingScreenshot,
  ]);

  return <FeedbackCtx.Provider value={value}>{children}</FeedbackCtx.Provider>;
}

export function useFeedbackContext(): FeedbackContextValue {
  const context = useContext(FeedbackCtx);
  if (!context) {
    throw new Error('useFeedbackContext must be used within a FeedbackProvider');
  }
  return context;
}
